import React, { useState } from 'react';
import Swal from 'sweetalert2';
import { FaFileExcel, FaDownload, FaUpload, FaInfoCircle, FaCalendarAlt } from 'react-icons/fa';

const ModalImportAnggota = ({ isOpen, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [showHelper, setShowHelper] = useState(false);

  const currentYear = new Date().getFullYear();

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      
      if (!validTypes.includes(selectedFile.type)) {
        Swal.fire({
          icon: 'error',
          title: 'File Tidak Valid',
          text: 'Harap upload file Excel (.xlsx, .xls) atau CSV'
        });
        return;
      }

      setFile(selectedFile);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      
      if (!validTypes.includes(droppedFile.type)) {
        Swal.fire({
          icon: 'error',
          title: 'File Tidak Valid',
          text: 'Harap upload file Excel (.xlsx, .xls) atau CSV'
        });
        return;
      }

      setFile(droppedFile);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/import/template/anggota`);
      
      if (!response.ok) {
        throw new Error('Gagal mengunduh template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Template_Import_Anggota.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      Swal.fire({
        icon: 'success',
        title: 'Berhasil',
        text: 'Template berhasil diunduh!',
        timer: 2000
      });

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error.message || 'Gagal mengunduh template'
      });
    }
  };

  const handleImport = async () => {
    if (!file) {
      Swal.fire({
        icon: 'warning',
        title: 'Peringatan',
        text: 'Harap pilih file terlebih dahulu!'
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/import/anggota`, {
        method: 'POST',
        body: formData
      });

      const result = await response.json();

      if (response.ok || response.status === 207) {
        // Tampilkan hasil detail
        let htmlMessage = `
          <div style="text-align: left;">
            <p><strong>Total Data:</strong> ${result.total}</p>
            <p><strong>Berhasil:</strong> <span style="color: green;">${result.success}</span></p>
            <p><strong>Gagal:</strong> <span style="color: red;">${result.failed}</span></p>
        `;

        if (result.errors && result.errors.length > 0) {
          htmlMessage += `
            <hr>
            <p><strong>Detail Error:</strong></p>
            <div style="max-height: 200px; overflow-y: auto; text-align: left;">
              <ul>
          `;
          result.errors.forEach(err => {
            htmlMessage += `<li><strong>Baris ${err.row}:</strong> ${err.error}</li>`;
          });
          htmlMessage += `</ul></div>`;
        }

        htmlMessage += `</div>`;

        Swal.fire({
          icon: result.failed === 0 ? 'success' : 'warning',
          title: 'Import Selesai',
          html: htmlMessage,
          width: 600
        });

        if (result.success > 0) {
          onSuccess();
          setFile(null);
          if (result.failed === 0) {
            onClose();
          }
        }

      } else {
        throw new Error(result.message || 'Gagal mengimport data');
      }

    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Gagal',
        text: error.message || 'Terjadi kesalahan saat mengimport data'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal is-active">
      <div className="modal-background" onClick={handleClose}></div>
      <div className="modal-card" style={{ width: '600px' }}>
        <header className="modal-card-head">
          <p className="modal-card-title">
            <FaFileExcel style={{ marginRight: '10px' }} />
            Import Data Anggota
          </p>
          <button className="delete" aria-label="close" onClick={handleClose}></button>
        </header>
        
        <section className="modal-card-body">
          {/* Informasi */}
          <article className="message is-info">
            <div className="message-body">
              <FaInfoCircle style={{ marginRight: '5px' }} />
              <strong>Panduan Import:</strong>
              <ol style={{ marginTop: '10px', marginLeft: '20px' }}>
                <li>Download template Excel terlebih dahulu</li>
                <li>Isi data anggota sesuai format template</li>
                <li><strong>PENTING:</strong> Username & Password otomatis = NIS</li>
                <li><strong>Tahun Masuk WAJIB diisi untuk siswa</strong> (contoh: 2022, 2023, 2024)</li>
                <li>Upload file yang sudah diisi</li>
                <li>Sistem akan memvalidasi dan mengimport data</li>
              </ol>
              <div style={{ marginTop: '10px', padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px' }}>
                <strong>💡 Tips Tahun Masuk (2025):</strong><br />
                • Kelas 9 → Masuk 2022 | Kelas 8 → Masuk 2023 | Kelas 7 → Masuk 2024/2025
              </div>
            </div>
          </article>

          {/* Download Template */}
          <div className="field">
            <button 
              className="button is-link is-fullwidth"
              onClick={handleDownloadTemplate}
            >
              <span className="icon">
                <FaDownload />
              </span>
              <span>Download Template Excel</span>
            </button>
          </div>

          <hr />

          {/* Tabel Bantuan Tahun Masuk
          <div className="field">
            <button 
              className="button is-light is-fullwidth"
              onClick={() => setShowHelper(!showHelper)}
              type="button"
            >
              <span className="icon">
                <FaCalendarAlt />
              </span>
              <span>{showHelper ? 'Sembunyikan' : 'Tampilkan'} Panduan Tahun Masuk</span>
            </button>
          </div>

          {showHelper && (
            <div className="box" style={{ backgroundColor: '#f9f9f9', marginTop: '10px' }}>
              <p className="title is-6">📅 Referensi Tahun Masuk (Tahun {currentYear})</p>
              <table className="table is-narrow is-fullwidth">
                <thead>
                  <tr>
                    <th>Kelas Sekarang</th>
                    <th>Tahun Masuk</th>
                    <th>Tahun Lulus</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><span className="tag is-danger">Kelas 9</span></td>
                    <td><strong>{currentYear - 3}</strong></td>
                    <td>{currentYear}</td>
                  </tr>
                  <tr>
                    <td><span className="tag is-warning">Kelas 8</span></td>
                    <td><strong>{currentYear - 2}</strong></td>
                    <td>{currentYear + 1}</td>
                  </tr>
                  <tr>
                    <td><span className="tag is-info">Kelas 7 (Lama)</span></td>
                    <td><strong>{currentYear - 1}</strong></td>
                    <td>{currentYear + 2}</td>
                  </tr>
                  <tr>
                    <td><span className="tag is-success">Kelas 7 (Baru)</span></td>
                    <td><strong>{currentYear}</strong></td>
                    <td>{currentYear + 3}</td>
                  </tr>
                  <tr>
                    <td><span className="tag">Guru</span></td>
                    <td colSpan="2"><em className="has-text-grey">Kosongkan</em></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          <hr /> */}

          {/* Upload Area */}
          <div className="field">
            <label className="label">Upload File Excel/CSV</label>
            <div
              className={`box has-text-centered ${dragActive ? 'has-background-light' : ''}`}
              style={{
                border: '2px dashed #dbdbdb',
                padding: '40px',
                cursor: 'pointer',
                transition: 'all 0.3s'
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <FaUpload size={40} style={{ color: '#3273dc', marginBottom: '10px' }} />
              
              {file ? (
                <div>
                  <p className="has-text-weight-bold has-text-success">
                    File terpilih: {file.name}
                  </p>
                  <p className="has-text-grey-light" style={{ fontSize: '0.9em' }}>
                    Ukuran: {(file.size / 1024).toFixed(2)} KB
                  </p>
                  <button
                    className="button is-small is-danger is-light mt-3"
                    onClick={(e) => {
                      e.stopPropagation();
                      setFile(null);
                    }}
                  >
                    Hapus File
                  </button>
                </div>
              ) : (
                <div>
                  <p>Drag & drop file di sini atau</p>
                  <label className="button is-primary is-light mt-3">
                    Pilih File
                    <input
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                  <p className="help mt-2">Format: .xlsx, .xls, atau .csv (Max 5MB)</p>
                </div>
              )}
            </div>
          </div>

          {/* Tombol Import */}
          <div className="field">
            <button
              className={`button is-success is-fullwidth ${loading ? 'is-loading' : ''}`}
              onClick={handleImport}
              disabled={loading || !file}
            >
              <span className="icon">
                <FaUpload />
              </span>
              <span>Import Data</span>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ModalImportAnggota;